import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';

export default function Register() {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const { register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user came from pricing with a selected plan
    const savedPlan = localStorage.getItem('selectedPlan');
    if (savedPlan) {
      try {
        const planData = JSON.parse(savedPlan);
        // Check if it's still valid (within 30 minutes)
        if (Date.now() - planData.timestamp < 30 * 60 * 1000) {
          setSelectedPlan(planData.plan);
        } else {
          localStorage.removeItem('selectedPlan');
        }
      } catch (e) {
        localStorage.removeItem('selectedPlan');
      }
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    try {
      const result = await register(username, email, password, currency, phone);
      
      if (result && result.success) {
        toast.success('Account created!');
        
        // If user had a selected plan, activate subscription
        if (selectedPlan) {
          try {
            const response = await api.post('/subscribe', {
              plan: selectedPlan,
              phone: phone
            });
            
            if (response.data.success) {
              toast.success('Subscription activated! Redirecting to dashboard...');
              localStorage.removeItem('selectedPlan');
              setTimeout(() => {
                navigate('/dashboard');
              }, 1500);
              return;
            }
          } catch (error) {
            console.error('Subscription activation error:', error);
            toast.error('Failed to activate subscription. Please go to Pricing.');
          }
        }
        
        // No plan selected or activation failed, go to pricing
        navigate('/pricing');
      } else {
        toast.error(result?.error || 'Registration failed. Please try again.');
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast.error('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = (provider) => {
    toast.info(provider + ' login coming soon! For now, use email registration.');
  };

  const openWhatsApp = () => {
    window.open('https://wa.me/254721669850', '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-800 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <div className="backdrop-blur-lg bg-white/10 rounded-2xl shadow-2xl p-8 border border-white/20">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-2xl shadow-xl mb-4 animate-pulse">
              <svg className="w-12 h-12 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-4xl font-extrabold text-white">FinovaTrack</h2>
            <p className="mt-2 text-lg text-indigo-100">Create your account</p>
            {selectedPlan && (
              <div className="mt-2 text-sm text-yellow-200 bg-yellow-500/20 px-3 py-1 rounded-full inline-block">
                🎯 Plan selected: {selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)}
              </div>
            )}
          </div>

          <div className="mb-6 p-3 bg-indigo-500/20 border border-indigo-400 rounded-lg text-center">
            <p className="text-indigo-200 font-semibold">🔓 Get Started Immediately</p>
            <p className="text-indigo-100 text-sm">Create your account and start using FinovaTrack now. Payment integration coming soon!</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-white mb-2">Username</label>
              <input 
                type="text" 
                required 
                value={username} 
                onChange={(e) => setUsername(e.target.value)} 
                className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-xl text-white placeholder-white/50 focus:ring-2 focus:ring-indigo-500 focus:border-transparent" 
                placeholder="Enter username" 
                disabled={loading} 
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-white mb-2">Email Address</label>
              <input 
                type="email" 
                required 
                value={email} 
                onChange={(e) => setEmail(e.target.value)} 
                className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-xl text-white placeholder-white/50 focus:ring-2 focus:ring-indigo-500 focus:border-transparent" 
                placeholder="your@email.com" 
                disabled={loading} 
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-white mb-2">Phone Number</label>
              <input 
                type="tel" 
                value={phone} 
                onChange={(e) => setPhone(e.target.value)} 
                className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-xl text-white placeholder-white/50 focus:ring-2 focus:ring-indigo-500 focus:border-transparent" 
                placeholder="0712345678" 
              />
              <p className="mt-1 text-xs text-indigo-300">For M-Pesa payment (coming soon)</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-white mb-2">Password</label>
              <input 
                type="password" 
                required 
                value={password} 
                onChange={(e) => setPassword(e.target.value)} 
                className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-xl text-white placeholder-white/50 focus:ring-2 focus:ring-indigo-500 focus:border-transparent" 
                placeholder="••••••••" 
                disabled={loading} 
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-white mb-2">Confirm Password</label>
              <input 
                type="password" 
                required 
                value={confirmPassword} 
                onChange={(e) => setConfirmPassword(e.target.value)} 
                className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-xl text-white placeholder-white/50 focus:ring-2 focus:ring-indigo-500 focus:border-transparent" 
                placeholder="••••••••" 
                disabled={loading} 
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-white mb-2">Currency</label>
              <select 
                value={currency} 
                onChange={(e) => setCurrency(e.target.value)} 
                className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-xl text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent" 
                disabled={loading}
              >
                <option value="USD">💵 USD - US Dollar</option>
                <option value="EUR">💶 EUR - Euro</option>
                <option value="GBP">💷 GBP - British Pound</option>
                <option value="CAD">🇨🇦 CAD - Canadian Dollar</option>
                <option value="AUD">🇦🇺 AUD - Australian Dollar</option>
                <option value="KES">🇰🇪 KES - Kenyan Shilling</option>
                <option value="NGN">🇳🇬 NGN - Nigerian Naira</option>
                <option value="ZAR">🇿🇦 ZAR - South African Rand</option>
              </select>
            </div>
            
            <button 
              type="submit" 
              disabled={loading} 
              className="w-full py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold hover:from-indigo-700 hover:to-purple-700 transform hover:scale-105 transition-all duration-300 disabled:opacity-50"
            >
              {loading ? 'Creating Account...' : selectedPlan ? 'Create Account & Activate Plan' : 'Create Account'}
            </button>
          </form>

          {/* Social Login Section */}
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/20"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-transparent text-white/70">Or continue with</span>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-3">
              <button onClick={() => handleSocialLogin('Google')} className="flex items-center justify-center px-4 py-2 border border-white/30 rounded-lg shadow-sm text-sm font-medium text-white bg-white/10 hover:bg-white/20 transform hover:scale-105 transition-all duration-300">
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24"><path fill="white" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="white" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="white" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="white" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                Google
              </button>
              <button onClick={() => handleSocialLogin('Facebook')} className="flex items-center justify-center px-4 py-2 border border-white/30 rounded-lg shadow-sm text-sm font-medium text-white bg-white/10 hover:bg-white/20 transform hover:scale-105 transition-all duration-300">
                <svg className="w-5 h-5 mr-2" fill="white" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                Facebook
              </button>
              <button onClick={() => handleSocialLogin('GitHub')} className="flex items-center justify-center px-4 py-2 border border-white/30 rounded-lg shadow-sm text-sm font-medium text-white bg-white/10 hover:bg-white/20 transform hover:scale-105 transition-all duration-300">
                <svg className="w-5 h-5 mr-2" fill="white" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12c0 4.42 2.87 8.17 6.84 9.49.5.09.68-.21.68-.48 0-.24-.01-.88-.01-1.73-2.78.6-3.37-1.34-3.37-1.34-.46-1.16-1.11-1.47-1.11-1.47-.91-.62.07-.61.07-.61 1 .07 1.53 1.03 1.53 1.03.89 1.52 2.34 1.08 2.91.83.09-.65.35-1.09.63-1.34-2.22-.25-4.55-1.11-4.55-4.94 0-1.09.39-1.98 1.03-2.68-.1-.25-.45-1.27.1-2.64 0 0 .84-.27 2.75 1.02.8-.22 1.65-.33 2.5-.33.85 0 1.7.11 2.5.33 1.91-1.29 2.75-1.02 2.75-1.02.55 1.37.2 2.39.1 2.64.64.7 1.03 1.59 1.03 2.68 0 3.84-2.34 4.69-4.57 4.94.36.31.68.92.68 1.85 0 1.34-.01 2.42-.01 2.75 0 .27.18.58.69.48C19.13 20.17 22 16.42 22 12c0-5.52-4.48-10-10-10z"/></svg>
                GitHub
              </button>
            </div>
          </div>

          {/* WhatsApp Support Button */}
          <div className="mt-4">
            <button onClick={openWhatsApp} className="w-full py-2 bg-green-600/80 backdrop-blur-sm text-white rounded-lg font-medium hover:bg-green-700 transform hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12.032 2.016c-5.52 0-9.984 4.464-9.984 9.984 0 1.824.48 3.552 1.344 5.04L2.016 21.984l5.04-1.392c1.44.864 3.12 1.392 4.992 1.392 5.52 0 9.984-4.464 9.984-9.984s-4.464-9.984-9.984-9.984zm0 18.432c-1.68 0-3.264-.48-4.656-1.344l-.336-.192-3.024.864.864-2.976-.192-.336c-.96-1.44-1.536-3.072-1.536-4.8 0-4.704 3.84-8.544 8.544-8.544s8.544 3.84 8.544 8.544-3.84 8.544-8.544 8.544zm4.992-6.624c-.288-.144-1.728-.864-1.968-.96-.288-.096-.48-.144-.672.144-.192.288-.768.96-.96 1.152-.144.144-.288.192-.576.048-.288-.144-1.2-.432-2.256-1.392-.864-.768-1.44-1.728-1.584-2.016-.144-.288-.016-.432.096-.576.096-.096.192-.288.288-.432.096-.144.144-.24.192-.384.048-.144.024-.288-.024-.432-.048-.144-.672-1.632-.96-2.208-.24-.528-.48-.432-.672-.432-.144 0-.336-.048-.528-.048-.192 0-.48.048-.72.24-.24.192-1.008.96-1.008 2.352 0 1.392 1.008 2.736 1.152 2.928.144.192 1.968 3.072 4.752 4.224.672.288 1.2.48 1.632.624.672.192 1.296.144 1.776.096.528-.048 1.632-.672 1.872-1.344.24-.672.24-1.248.144-1.344-.096-.096-.288-.144-.576-.288z"/></svg>
              Need Help? Chat on WhatsApp
            </button>
          </div>

          <div className="mt-4 text-center">
            <Link to="/login" className="text-sm text-indigo-200 hover:text-white transition">
              Already have an account? Sign in
            </Link>
          </div>
        </div>

        {/* Social Media Links */}
        <div className="mt-6 text-center">
          <p className="text-indigo-200 text-sm mb-3">Follow FinovaTrack</p>
          <div className="flex justify-center space-x-4">
            <a href="https://www.facebook.com/share/1TPryMfeEC/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" className="bg-white/10 backdrop-blur-sm p-3 rounded-full hover:bg-white/20 transition-all duration-300 hover:scale-110">
              <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
            </a>
            <a href="https://www.instagram.com/budgettracker96" target="_blank" rel="noopener noreferrer" className="bg-white/10 backdrop-blur-sm p-3 rounded-full hover:bg-white/20 transition-all duration-300 hover:scale-110">
              <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
            </a>
            <a href="https://www.tiktok.com/@budgettracker6" target="_blank" rel="noopener noreferrer" className="bg-white/10 backdrop-blur-sm p-3 rounded-full hover:bg-white/20 transition-all duration-300 hover:scale-110">
              <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 01-.03-3.06 2.86 2.86 0 011.69-1.33 2.89 2.89 0 011.35-.1V9.37a6.32 6.32 0 00-4.58 1.54 6.32 6.32 0 00-1.82 4.62 6.32 6.32 0 006.32 6.32 6.32 6.32 0 006.32-6.32V10.6a7.89 7.89 0 004.05 1.11V8.25a4.84 4.84 0 01-2.2-1.56z"/></svg>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

